import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import DocumentHeader from 'Components/DocumentHeader';
import DocumentWrapper from 'Components/DocumentWrapper';
import HistorySection from 'Components/HistorySection';
import HistoryRow from 'Components/HistoryRow';
import HistoryRowControls from 'Components/HistoryRowControls';
import FormEditingHandler from 'Containers/FormEditingHandler';
import HistoryRowData from './HistoryRowData';
import Form from './Form';
import { formatPartialDate } from 'Utility/date';
import DocumentCreationButton from 'Components/DocumentCreationButton';
import { isEnded } from 'Utility/isEnded';
import { filterByDocumentType, sortDocumentsDescending } from 'Utility/documentHandling';

const activeTherapy = (
  documents: IDocumentWithTypeV2<'therapy'>[],
  startEdit: StartEdit,
): React.JSX.Element | undefined => {
  return documents.length > 0 ? (
    <HistorySection headerText={<FormattedMessage id="therapy.activeTherapies" />} hasHistoryRows={true}>
      {documents.toSorted(sortDocumentsDescending).map((d, i) => (
        <HistoryRow
          headerText={
            <div id={'therapyDate'}>
              {d.date ? (
                <FormattedMessage id="therapy.startingDate" values={{ date: formatPartialDate(d.date) }} />
              ) : undefined}
            </div>
          }
          key={i + 'active'}
          rowButton={<HistoryRowControls document={d} startEdit={startEdit} />}
          controlsMargin={false}
        >
          <HistoryRowData document={d} />
        </HistoryRow>
      ))}
    </HistorySection>
  ) : undefined;
};

const endedTherapy = (
  documents: IDocumentWithTypeV2<'therapy'>[],
  startEdit: StartEdit,
): React.JSX.Element | undefined => {
  return documents.length > 0 ? (
    <HistorySection
      headerText={<FormattedMessage id="therapy.inActiveTherapies" />}
      inactive={true}
      hasHistoryRows={true}
    >
      {documents.toSorted(sortDocumentsDescending).map((d, i) => (
        <HistoryRow
          headerText={
            d.date ? formatPartialDate(d.date) + ' - ' + (d.endDate ? formatPartialDate(d.endDate) : '') : undefined
          }
          key={i + 'active'}
          rowButton={<HistoryRowControls document={d} startEdit={startEdit} />}
          controlsMargin={false}
        >
          <HistoryRowData document={d} />
        </HistoryRow>
      ))}
    </HistorySection>
  ) : undefined;
};

const Therapy = ({ documents }: IOwnProps): React.JSX.Element => {
  const therapyDocs = filterByDocumentType(documents, 'therapy');
  return (
    <FormEditingHandler name="therapy" documents={documents}>
      {(editing, startEdit, formData): React.JSX.Element => (
        <DocumentWrapper>
          <DocumentHeader
            name="therapy"
            headerId={'therapy.title'}
            editing={editing}
            editButtons={
              <div>
                <DocumentCreationButton
                  name="therapy"
                  text={'general.new'}
                  onClick={startEdit({})}
                  asyncOnClick={undefined}
                />
              </div>
            }
          />
          {!editing && therapyDocs.length ? (
            <div>
              {activeTherapy(
                therapyDocs.filter(d => !isEnded(d.endDate)),
                startEdit,
              )}
              {endedTherapy(
                therapyDocs.filter(d => isEnded(d.endDate)),
                startEdit,
              )}
            </div>
          ) : undefined}
          {editing ? (
            <Form formData={formData} editing={editing} document={therapyDocs.find(d => d._id === editing)} />
          ) : undefined}
        </DocumentWrapper>
      )}
    </FormEditingHandler>
  );
};

interface IOwnProps {
  documents: IDocumentWithTypeV2<'therapy'>[];
}

export default Therapy;
